import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Bold,
  ChevronDown,
  ChevronUp,
  Code,
  Copy,
  GripVertical,
  Heading as HeadingIcon,
  Image as ImageIcon,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Minus,
  MousePointerClick,
  MoveVertical,
  Table as TableIcon,
  Trash2,
  Type,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight
} from 'lucide-react';
import CodeEditor from './CodeEditor';

/* ------------------------------------------------------------------ */
/* Block model                                                         */
/* ------------------------------------------------------------------ */

const MARKER_PREFIX = '<!--dp-email-builder:v1:';
const MARKER_RE = /<!--dp-email-builder:v1:([A-Za-z0-9+/=]*)(?::([0-9a-f]+))?-->/;

// Fingerprint of the email body (whitespace ignored) so we can tell if someone
// edited the generated HTML by hand, without being thrown off by trimming/reformatting.
const fingerprint = (str) => {
  let h = 5381;
  const t = String(str).replace(/\s+/g, '');
  for (let i = 0; i < t.length; i += 1) h = ((h << 5) + h + t.charCodeAt(i)) >>> 0;
  return h.toString(16);
};

const uid = () => Math.random().toString(36).slice(2, 10);

const PALETTE = [
  { key: 'heading', type: 'heading', label: 'Heading', icon: HeadingIcon },
  { key: 'text', type: 'text', label: 'Text', icon: Type },
  { key: 'bullets', type: 'text', preset: 'bullets', label: 'Bullet list', icon: List },
  { key: 'numbers', type: 'text', preset: 'numbers', label: 'Numbered list', icon: ListOrdered },
  { key: 'table', type: 'table', label: 'Table', icon: TableIcon },
  { key: 'image', type: 'image', label: 'Image', icon: ImageIcon },
  { key: 'button', type: 'button', label: 'Button', icon: MousePointerClick },
  { key: 'divider', type: 'divider', label: 'Divider', icon: Minus },
  { key: 'spacer', type: 'spacer', label: 'Spacer', icon: MoveVertical },
  { key: 'html', type: 'html', label: 'Custom HTML', icon: Code }
];

const newBlock = (type, preset) => {
  const id = uid();
  switch (type) {
    case 'heading':
      return { id, type, text: 'Your heading', level: 2, align: 'left', color: '#111827' };
    case 'text':
      if (preset === 'bullets') {
        return { id, type, html: '<ul><li>First point</li><li>Second point</li><li>Third point</li></ul>' };
      }
      if (preset === 'numbers') {
        return { id, type, html: '<ol><li>First step</li><li>Second step</li><li>Third step</li></ol>' };
      }
      return { id, type, html: '<p>Write your message here. Select text to make it bold, italic or a list.</p>' };
    case 'image':
      return { id, type, src: '', alt: '', link: '', width: 100, align: 'center' };
    case 'button':
      return { id, type, label: 'Click here', url: '', bg: '#0d9488', color: '#ffffff', align: 'center' };
    case 'divider':
      return { id, type };
    case 'spacer':
      return { id, type, height: 24 };
    case 'table':
      return {
        id,
        type,
        header: true,
        rows: [
          ['Item', 'Details'],
          ['', ''],
          ['', '']
        ]
      };
    case 'html':
      return { id, type, html: '' };
    default:
      return { id, type: 'text', html: '<p></p>' };
  }
};

/* ------------------------------------------------------------------ */
/* HTML generation / parsing                                           */
/* ------------------------------------------------------------------ */

const esc = (s) =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const encodeBlocks = (blocks) => {
  try {
    return btoa(unescape(encodeURIComponent(JSON.stringify(blocks))));
  } catch (e) {
    return '';
  }
};

const decodeBlocks = (b64) => {
  try {
    const parsed = JSON.parse(decodeURIComponent(escape(atob(b64))));
    return Array.isArray(parsed) ? parsed : null;
  } catch (e) {
    return null;
  }
};

// Replaced with the campaign fonts (Themes & Layout) or the system default when previewed/sent.
const FONT_BODY = '{{paragraph_font}}';
const FONT_HEAD = '{{header_font}}';

const RICH_BASE_STYLE = {
  p: 'margin:0 0 12px 0;',
  ul: 'margin:0 0 12px 0;padding-left:24px;',
  ol: 'margin:0 0 12px 0;padding-left:24px;',
  li: 'margin:0 0 4px 0;',
  b: 'font-weight:700;color:#111827;',
  strong: 'font-weight:700;color:#111827;'
};

// Turns the editor's raw markup into email-safe HTML with inline styles.
const styleRichHtml = (html) =>
  (html || '')
    // Chrome can wrap a list in <p>/<div> (e.g. when aligned); lists can't live inside a paragraph.
    .replace(/<(p|div)([^>]*)>\s*<(ul|ol)>([\s\S]*?)<\/\3>\s*<\/\1>/g, '<$3$2>$4</$3>')
    .replace(/<div([^>]*)>/g, '<p$1>')
    .replace(/<\/div>/g, '</p>')
    .replace(/<(p|ul|ol|li|b|strong)((?:\s[^>]*)?)>/g, (m, tag, attrs) => {
      const existing = attrs.match(/style="([^"]*)"/);
      const merged = RICH_BASE_STYLE[tag] + (existing ? existing[1] : '');
      return existing
        ? `<${tag}${attrs.replace(existing[0], `style="${merged}"`)}>`
        : `<${tag}${attrs} style="${merged}">`;
    })
    .replace(/font-weight:\s*bold;?/g, 'font-weight:700;color:#111827;')
    .replace(/<a /g, '<a style="color:#0d9488;" ');

const HEADING_SIZES = { 1: 30, 2: 24, 3: 18 };

const blockToHtml = (b) => {
  switch (b.type) {
    case 'heading': {
      const size = HEADING_SIZES[b.level] || 24;
      return `<h${b.level} style="margin:0;font-family:${FONT_HEAD};font-size:${size}px;line-height:1.3;color:${esc(b.color)};text-align:${b.align};">${esc(b.text)}</h${b.level}>`;
    }
    case 'text':
      return `<div style="font-family:${FONT_BODY};font-size:16px;line-height:1.6;color:#4b5563;">${styleRichHtml(b.html)}</div>`;
    case 'image': {
      if (!b.src) return '';
      const img = `<img src="${esc(b.src)}" alt="${esc(b.alt)}" style="display:inline-block;width:${Number(b.width) || 100}%;max-width:100%;height:auto;border:0;" />`;
      const inner = b.link ? `<a href="${esc(b.link)}" target="_blank">${img}</a>` : img;
      return `<div style="text-align:${b.align};">${inner}</div>`;
    }
    case 'button':
      return `<div style="text-align:${b.align};"><a href="${esc(b.url || '#')}" target="_blank" style="display:inline-block;padding:12px 28px;background:${esc(b.bg)};color:${esc(b.color)};font-family:${FONT_BODY};font-size:16px;font-weight:bold;text-decoration:none;border-radius:6px;">${esc(b.label)}</a></div>`;
    case 'divider':
      return '<hr data-dp-keep style="border:0;border-top:1px solid #e5e7eb;margin:0;" />';
    case 'spacer':
      return `<div style="height:${Number(b.height) || 24}px;line-height:${Number(b.height) || 24}px;font-size:1px;">&nbsp;</div>`;
    case 'table': {
      const rows = b.rows
        .map((row, r) => {
          const isHead = b.header && r === 0;
          const tag = isHead ? 'th' : 'td';
          const cells = row
            .map(
              (c) =>
                `<${tag} data-dp-keep style="border:1px solid #d1d5db;padding:8px 12px;text-align:left;font-family:${FONT_BODY};font-size:15px;color:#374151;${isHead ? 'background:#f3f4f6;font-weight:bold;' : ''}">${esc(c)}</${tag}>`
            )
            .join('');
          return `<tr>${cells}</tr>`;
        })
        .join('');
      return `<table data-dp-keep role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;width:100%;">${rows}</table>`;
    }
    case 'html':
      return b.html || '';
    default:
      return '';
  }
};

const blocksToHtml = (blocks) => {
  if (!blocks.length) return '';
  const rows = blocks
    .map((b) => {
      const inner = blockToHtml(b);
      return inner ? `<tr><td style="padding:8px 24px;">${inner}</td></tr>` : '';
    })
    .join('\n');
  const body =
    `<style>{{font_face_css}}</style>\n` +
    `<div style="background:#f4f4f5;padding:24px 0;">\n` +
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr><td align="center">\n` +
    `<table role="presentation" width="600" cellpadding="0" cellspacing="0" style="width:100%;max-width:600px;background:#ffffff;border-radius:8px;">\n` +
    `<tr><td style="height:16px;line-height:16px;font-size:1px;">&nbsp;</td></tr>\n${rows}\n` +
    `<tr><td style="height:16px;line-height:16px;font-size:1px;">&nbsp;</td></tr>\n` +
    `</table>\n</td></tr></table>\n</div>`;
  return `${MARKER_PREFIX}${encodeBlocks(blocks)}:${fingerprint(body)}-->\n${body}`;
};

const withFreshIds = (blocks) => blocks.map((b) => ({ ...b, id: uid() }));

/** Decide how an incoming HTML string should open in the editor. */
const interpretValue = (value) => {
  const html = typeof value === 'string' ? value : '';
  if (!html.trim()) return { mode: 'visual', blocks: [] };
  const m = html.match(MARKER_RE);
  if (m) {
    const blocks = decodeBlocks(m[1]);
    // m[2] is the fingerprint saved with the email; emails saved before it existed have none.
    const body = html.slice(html.indexOf('-->', m.index) + 3);
    const untouched = !m[2] || m[2] === fingerprint(body);
    if (blocks && untouched) {
      const split = blocks.length === 1 && blocks[0].type === 'html' ? htmlToBlocks(blocks[0].html) : blocks;
      return { mode: 'visual', blocks: withFreshIds(split) };
    }
  }
  return { mode: 'html', blocks: null };
};

/* ------------------------------------------------------------------ */
/* HTML -> blocks (best effort, used for hand-written / wrapped HTML)   */
/* ------------------------------------------------------------------ */

const styleOf = (el, prop) => {
  const m = (el.getAttribute('style') || '').match(new RegExp(`(?:^|;)\\s*${prop}\\s*:\\s*([^;]+)`, 'i'));
  return m ? m[1].trim() : '';
};

const alignOf = (el) => {
  for (let n = el; n && n.nodeType === 1; n = n.parentElement) {
    const a = styleOf(n, 'text-align') || n.getAttribute('align') || '';
    if (['left', 'center', 'right'].includes(a.toLowerCase())) return a.toLowerCase();
  }
  return 'center';
};

const unstyleRich = (html) => {
  let out = html;
  Object.values(RICH_BASE_STYLE).forEach((base) => {
    out = out.split(base).join('');
  });
  return out.replace(/\sstyle="\s*"/g, '');
};

const isButtonLink = (a) => {
  const st = (a.getAttribute('style') || '').toLowerCase();
  return /display\s*:\s*inline-block/.test(st) && /background/.test(st) && /padding/.test(st) && !a.querySelector('img');
};

const isDataTable = (t) => {
  if (t.querySelector('table')) return false;
  const rows = [...t.querySelectorAll('tr')];
  if (rows.length < 2) return false;
  const widths = rows.map((r) => r.children.length);
  if (widths[0] < 2 || widths.some((w) => w !== widths[0])) return false;
  return ![...t.querySelectorAll('td, th')].some((c) => c.querySelector('img, h1, h2, h3, hr, a, ul, ol, p, div'));
};

const hasSpecial = (el) =>
  !!el.querySelector('table, img, h1, h2, h3, hr') || [...el.querySelectorAll('a')].some(isButtonLink);

const htmlToBlocks = (html) => {
  const tpl = document.createElement('template');
  tpl.innerHTML = String(html || '').replace(MARKER_RE, '');
  const blocks = [];
  let run = [];

  const flush = () => {
    const joined = run.join('').trim();
    run = [];
    if (!joined.replace(/<[^>]*>|&nbsp;|\s/g, '')) return;
    const b = newBlock('text');
    b.html = /^\s*<(p|ul|ol|div|h[1-6])\b/i.test(joined) ? unstyleRich(joined) : `<p>${unstyleRich(joined)}</p>`;
    blocks.push(b);
  };
  const push = (b) => {
    flush();
    blocks.push(b);
  };

  const heading = (el) => {
    const b = newBlock('heading');
    b.text = el.textContent.trim();
    b.level = el.tagName === 'H1' ? 1 : el.tagName === 'H2' ? 2 : 3;
    const a = (styleOf(el, 'text-align') || '').toLowerCase();
    if (['left', 'center', 'right'].includes(a)) b.align = a;
    const c = styleOf(el, 'color');
    if (/^#[0-9a-f]{3,8}$/i.test(c)) b.color = c;
    return b;
  };

  const image = (img, link) => {
    const b = newBlock('image');
    b.src = img.getAttribute('src') || '';
    b.alt = img.getAttribute('alt') || '';
    b.link = link || '';
    const w = styleOf(img, 'width').match(/^(\d+)%/);
    if (w) b.width = Math.min(100, Math.max(10, Number(w[1])));
    b.align = alignOf(img.parentElement || img);
    return b;
  };

  const button = (a) => {
    const b = newBlock('button');
    b.label = a.textContent.trim() || 'Click here';
    b.url = a.getAttribute('href') || '';
    b.bg = styleOf(a, 'background') || styleOf(a, 'background-color') || b.bg;
    b.color = styleOf(a, 'color') || b.color;
    b.align = alignOf(a.parentElement || a);
    return b;
  };

  const walk = (node) => {
    if (node.nodeType === 3) {
      if (node.textContent.trim()) run.push(node.textContent);
      return;
    }
    if (node.nodeType !== 1) return;
    const tag = node.tagName;
    if (['STYLE', 'SCRIPT', 'HEAD', 'TITLE', 'META', 'LINK'].includes(tag)) return;
    if (/^H[1-3]$/.test(tag)) return push(heading(node));
    if (tag === 'HR') return push(newBlock('divider'));
    if (tag === 'IMG') return push(image(node, ''));
    if (tag === 'A') {
      if (isButtonLink(node)) return push(button(node));
      const img = node.querySelector('img');
      if (img && !node.textContent.trim()) return push(image(img, node.getAttribute('href') || ''));
      run.push(node.outerHTML);
      return;
    }
    if (tag === 'TABLE') {
      if (isDataTable(node)) {
        const b = newBlock('table');
        const rows = [...node.querySelectorAll('tr')];
        b.rows = rows.map((r) => [...r.children].map((c) => c.textContent.trim()));
        b.header = [...rows[0].children].every((c) => c.tagName === 'TH');
        return push(b);
      }
      flush();
      [...node.childNodes].forEach(walk);
      flush();
      return;
    }
    if (['TBODY', 'THEAD', 'TFOOT', 'TR', 'TD', 'TH', 'DIV', 'CENTER', 'BODY', 'HTML', 'SECTION', 'MAIN', 'FONT'].includes(tag)) {
      if (!['TBODY', 'THEAD', 'TFOOT', 'TR'].includes(tag) && !hasSpecial(node)) {
        flush();
        const inner = node.innerHTML.trim();
        if (inner) {
          run.push(inner);
          flush();
        }
        return;
      }
      flush();
      [...node.childNodes].forEach(walk);
      flush();
      return;
    }
    run.push(node.outerHTML);
  };

  [...tpl.content.childNodes].forEach(walk);
  flush();

  // Anything we could not understand stays safe as its own Custom HTML section.
  if (!blocks.length && String(html || '').trim()) {
    const b = newBlock('html');
    b.html = String(html).replace(MARKER_RE, '').trim();
    return [b];
  }
  return blocks;
};

/* ------------------------------------------------------------------ */
/* Small UI pieces                                                     */
/* ------------------------------------------------------------------ */

const inputCls =
  'w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent';

const AlignPicker = ({ value, onChange }) => (
  <div className="inline-flex border border-gray-300 rounded-md overflow-hidden">
    {[
      ['left', AlignLeft],
      ['center', AlignCenter],
      ['right', AlignRight]
    ].map(([v, Icon]) => (
      <button
        key={v}
        type="button"
        title={`Align ${v}`}
        onClick={() => onChange(v)}
        className={`px-2 py-1 ${value === v ? 'bg-teal-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
      >
        <Icon className="h-4 w-4" />
      </button>
    ))}
  </div>
);

const Field = ({ label, children, className = '' }) => (
  <label className={`block text-xs font-medium text-gray-600 ${className}`}>
    <span className="block mb-1">{label}</span>
    {children}
  </label>
);

const RichText = ({ html, onChange }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) ref.current.innerHTML = html || '';
    // set once on mount; later edits come from the user typing in the element
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      onFocus={() => {
        try {
          document.execCommand('defaultParagraphSeparator', false, 'p');
        } catch (e) {
          /* ignore */
        }
      }}
      onInput={(e) => onChange(e.currentTarget.innerHTML)}
      onPaste={(e) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain');
        document.execCommand('insertText', false, text);
      }}
      className="email-builder-rich min-h-[56px] px-3 py-2 text-base leading-relaxed focus:outline-none"
    />
  );
};

const RichToolbar = () => {
  const [, setTick] = useState(0);

  // Re-read formatting state whenever the selection moves so buttons can show as active.
  useEffect(() => {
    const onSel = () => setTick((t) => t + 1);
    document.addEventListener('selectionchange', onSel);
    return () => document.removeEventListener('selectionchange', onSel);
  }, []);

  const isOn = (cmd) => {
    try {
      return document.queryCommandState(cmd);
    } catch (e) {
      return false;
    }
  };

  const exec = (cmd, arg) => {
    try {
      // Write inline styles (font-weight etc.) so no page stylesheet can cancel the formatting.
      document.execCommand('styleWithCSS', false, true);
    } catch (e) {
      /* ignore */
    }
    document.execCommand(cmd, false, arg);
    setTick((t) => t + 1);
  };

  const run = (cmd, arg) => (e) => {
    e.preventDefault(); // keep the text selection
    exec(cmd, arg);
  };
  const addLink = (e) => {
    e.preventDefault();
    const url = window.prompt('Link address (e.g. https://example.com)');
    if (url) exec('createLink', url);
  };
  const Btn = ({ title, onMouseDown, active, children }) => (
    <button
      type="button"
      title={title}
      onMouseDown={onMouseDown}
      className={`p-1.5 rounded text-gray-700 ${active ? 'bg-teal-600 text-white' : 'hover:bg-gray-200'}`}
    >
      {children}
    </button>
  );
  return (
    <div className="flex flex-wrap items-center gap-0.5 px-2 py-1 bg-gray-50 border-b border-gray-200 rounded-t-lg">
      <Btn title="Bold" active={isOn('bold')} onMouseDown={run('bold')}><Bold className="h-4 w-4" /></Btn>
      <Btn title="Italic" active={isOn('italic')} onMouseDown={run('italic')}><Italic className="h-4 w-4" /></Btn>
      <Btn title="Underline" active={isOn('underline')} onMouseDown={run('underline')}><Underline className="h-4 w-4" /></Btn>
      <span className="w-px h-5 bg-gray-300 mx-1" />
      <Btn title="Bullet list" active={isOn('insertUnorderedList')} onMouseDown={run('insertUnorderedList')}><List className="h-4 w-4" /></Btn>
      <Btn title="Numbered list" active={isOn('insertOrderedList')} onMouseDown={run('insertOrderedList')}><ListOrdered className="h-4 w-4" /></Btn>
      <span className="w-px h-5 bg-gray-300 mx-1" />
      <Btn title="Align left" onMouseDown={run('justifyLeft')}><AlignLeft className="h-4 w-4" /></Btn>
      <Btn title="Align center" onMouseDown={run('justifyCenter')}><AlignCenter className="h-4 w-4" /></Btn>
      <Btn title="Align right" onMouseDown={run('justifyRight')}><AlignRight className="h-4 w-4" /></Btn>
      <span className="w-px h-5 bg-gray-300 mx-1" />
      <Btn title="Add link" onMouseDown={addLink}><LinkIcon className="h-4 w-4" /></Btn>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Block editors (what you see on the canvas)                          */
/* ------------------------------------------------------------------ */

const HeadingBlock = ({ block, update, selected }) => (
  <div>
    <input
      type="text"
      value={block.text}
      onChange={(e) => update({ text: e.target.value })}
      placeholder="Heading text"
      style={{
        fontSize: HEADING_SIZES[block.level],
        textAlign: block.align,
        color: block.color,
        fontWeight: 700
      }}
      className="w-full px-3 py-2 bg-transparent focus:outline-none"
    />
    {selected && (
      <div className="flex flex-wrap items-center gap-3 px-3 py-2 border-t border-gray-200 bg-gray-50 rounded-b-lg">
        <Field label="Size">
          <select
            value={block.level}
            onChange={(e) => update({ level: Number(e.target.value) })}
            className={inputCls}
          >
            <option value={1}>Large</option>
            <option value={2}>Medium</option>
            <option value={3}>Small</option>
          </select>
        </Field>
        <Field label="Alignment"><AlignPicker value={block.align} onChange={(align) => update({ align })} /></Field>
        <Field label="Colour">
          <input type="color" value={block.color} onChange={(e) => update({ color: e.target.value })} className="h-8 w-10 p-0 border border-gray-300 rounded" />
        </Field>
      </div>
    )}
  </div>
);

const TextBlock = ({ block, update, selected }) => (
  <div>
    {selected && <RichToolbar />}
    <RichText html={block.html} onChange={(html) => update({ html })} />
  </div>
);

const ImageBlock = ({ block, update, selected }) => {
  const previewable = block.src && !block.src.includes('{{');
  return (
    <div>
      <div className="px-3 py-3" style={{ textAlign: block.align }}>
        {previewable ? (
          <img
            src={block.src}
            alt={block.alt}
            style={{ display: 'inline-block', width: `${block.width}%`, maxWidth: '100%', height: 'auto' }}
          />
        ) : (
          <div className="inline-flex items-center justify-center w-full py-6 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500">
            <ImageIcon className="h-5 w-5 mr-2" />
            {block.src ? `Image: ${block.src}` : 'Add an image address below'}
          </div>
        )}
      </div>
      {selected && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 px-3 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <Field label="Image address (URL)" className="sm:col-span-2">
            <div className="flex gap-2">
              <input type="text" value={block.src} onChange={(e) => update({ src: e.target.value })} placeholder="https://…" className={inputCls} />
              <button
                type="button"
                onClick={() => update({ src: '{{campaign_logo}}' })}
                className="shrink-0 px-2 text-xs text-teal-700 bg-teal-100 rounded-md hover:bg-teal-200"
              >
                Use campaign logo
              </button>
            </div>
          </Field>
          <Field label="Description (alt text)">
            <input type="text" value={block.alt} onChange={(e) => update({ alt: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Link when clicked (optional)">
            <input type="text" value={block.link} onChange={(e) => update({ link: e.target.value })} placeholder="https://…" className={inputCls} />
          </Field>
          <Field label={`Width: ${block.width}%`}>
            <input type="range" min={10} max={100} step={5} value={block.width} onChange={(e) => update({ width: Number(e.target.value) })} className="w-full" />
          </Field>
          <Field label="Alignment"><AlignPicker value={block.align} onChange={(align) => update({ align })} /></Field>
        </div>
      )}
    </div>
  );
};

const ButtonBlock = ({ block, update, selected }) => (
  <div>
    <div className="px-3 py-4" style={{ textAlign: block.align }}>
      <span
        style={{ background: block.bg, color: block.color }}
        className="inline-block px-7 py-3 rounded-md font-bold"
      >
        <input
          type="text"
          value={block.label}
          onChange={(e) => update({ label: e.target.value })}
          size={Math.max(block.label.length, 6)}
          style={{ color: block.color }}
          className="bg-transparent text-center font-bold focus:outline-none"
        />
      </span>
    </div>
    {selected && (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 px-3 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
        <Field label="Where does the button go? (URL)" className="sm:col-span-2">
          <input type="text" value={block.url} onChange={(e) => update({ url: e.target.value })} placeholder="https://… or {{redemption_link}}" className={inputCls} />
        </Field>
        <Field label="Button colour">
          <input type="color" value={block.bg} onChange={(e) => update({ bg: e.target.value })} className="h-8 w-10 p-0 border border-gray-300 rounded" />
        </Field>
        <Field label="Text colour">
          <input type="color" value={block.color} onChange={(e) => update({ color: e.target.value })} className="h-8 w-10 p-0 border border-gray-300 rounded" />
        </Field>
        <Field label="Alignment"><AlignPicker value={block.align} onChange={(align) => update({ align })} /></Field>
      </div>
    )}
  </div>
);

const TableBlock = ({ block, update, selected }) => {
  const setCell = (r, c, v) =>
    update({ rows: block.rows.map((row, ri) => (ri === r ? row.map((cell, ci) => (ci === c ? v : cell)) : row)) });
  const cols = block.rows[0]?.length || 0;
  const smallBtn = 'px-2 py-1 text-xs rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-40';
  return (
    <div>
      <div className="px-3 py-3 overflow-x-auto">
        <table className="w-full border-collapse">
          <tbody>
            {block.rows.map((row, r) => (
              <tr key={r}>
                {row.map((cell, c) => {
                  const head = block.header && r === 0;
                  return (
                    <td key={c} className={`border border-gray-300 p-0 ${head ? 'bg-gray-100' : ''}`}>
                      <input
                        type="text"
                        value={cell}
                        onChange={(e) => setCell(r, c, e.target.value)}
                        placeholder={head ? 'Heading' : ''}
                        className={`w-full px-2 py-2 text-sm bg-transparent focus:outline-none focus:bg-teal-50 ${head ? 'font-bold' : ''}`}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {selected && (
        <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <button type="button" className={smallBtn} onClick={() => update({ rows: [...block.rows, Array(cols).fill('')] })}>+ Row</button>
          <button type="button" className={smallBtn} disabled={block.rows.length <= 1} onClick={() => update({ rows: block.rows.slice(0, -1) })}>− Row</button>
          <button type="button" className={smallBtn} onClick={() => update({ rows: block.rows.map((row) => [...row, '']) })}>+ Column</button>
          <button type="button" className={smallBtn} disabled={cols <= 1} onClick={() => update({ rows: block.rows.map((row) => row.slice(0, -1)) })}>− Column</button>
          <label className="flex items-center gap-1.5 text-xs text-gray-600 ml-2">
            <input type="checkbox" checked={block.header} onChange={(e) => update({ header: e.target.checked })} />
            First row is a heading
          </label>
        </div>
      )}
    </div>
  );
};

const DividerBlock = () => (
  <div className="px-3 py-4"><hr className="border-t border-gray-300" /></div>
);

const SpacerBlock = ({ block, update, selected }) => (
  <div>
    <div className="flex items-center justify-center text-xs text-gray-400" style={{ height: block.height }}>
      {block.height}px space
    </div>
    {selected && (
      <div className="px-3 py-2 border-t border-gray-200 bg-gray-50 rounded-b-lg">
        <Field label={`Height: ${block.height}px`}>
          <input type="range" min={8} max={96} step={4} value={block.height} onChange={(e) => update({ height: Number(e.target.value) })} className="w-full" />
        </Field>
      </div>
    )}
  </div>
);

const HtmlBlock = ({ block, update, split }) => (
  <div className="px-3 py-3">
    <div className="flex items-center justify-between mb-2">
      <p className="text-xs text-gray-500">Custom HTML — for advanced content only.</p>
      {split && (
        <button type="button" onClick={split} className="px-2 py-1 text-xs text-teal-700 bg-teal-100 rounded-md hover:bg-teal-200">
          Split into sections
        </button>
      )}
    </div>
    <textarea
      value={block.html}
      onChange={(e) => update({ html: e.target.value })}
      rows={6}
      spellCheck={false}
      className="w-full px-2 py-1.5 font-mono text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500"
    />
  </div>
);

const BLOCK_VIEWS = {
  heading: HeadingBlock,
  text: TextBlock,
  image: ImageBlock,
  button: ButtonBlock,
  table: TableBlock,
  divider: DividerBlock,
  spacer: SpacerBlock,
  html: HtmlBlock
};

const BLOCK_LABELS = {
  heading: 'Heading',
  text: 'Text',
  image: 'Image',
  button: 'Button',
  table: 'Table',
  divider: 'Divider',
  spacer: 'Spacer',
  html: 'Custom HTML'
};

/* ------------------------------------------------------------------ */
/* Drag & drop wrappers                                                */
/* ------------------------------------------------------------------ */

const PaletteItem = ({ item, onAdd }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette:${item.key}`,
    data: { palette: item }
  });
  const Icon = item.icon;
  return (
    <button
      ref={setNodeRef}
      type="button"
      {...attributes}
      {...listeners}
      onClick={() => onAdd(item)}
      title="Drag onto the email, or click to add at the end"
      className={`flex flex-col items-center justify-center gap-1 px-2 py-3 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg cursor-grab hover:border-teal-500 hover:text-teal-700 touch-none ${isDragging ? 'opacity-40' : ''}`}
    >
      <Icon className="h-5 w-5" />
      {item.label}
    </button>
  );
};

const Canvas = ({ children, onFocus, onBackgroundClick }) => {
  const { setNodeRef, isOver } = useDroppable({ id: 'canvas' });
  return (
    <div
      ref={setNodeRef}
      onFocus={onFocus}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onBackgroundClick();
      }}
      className={`min-h-[320px] max-h-[640px] overflow-y-auto p-4 rounded-lg border ${isOver ? 'border-teal-500' : 'border-gray-200'}`}
      style={{ background: '#f4f4f5' }}
    >
      {children}
    </div>
  );
};

const SortableBlock = ({ block, index, count, selected, onSelect, update, remove, duplicate, move, splitBlock }) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: block.id
  });
  const View = BLOCK_VIEWS[block.type];
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 }}
      onMouseDown={onSelect}
      className={`group relative bg-white rounded-lg border ${selected ? 'border-teal-500 ring-1 ring-teal-500' : 'border-transparent hover:border-gray-300'}`}
    >
      <div
        className={`absolute -top-3 right-2 z-10 flex items-center gap-0.5 px-1 py-0.5 bg-white border border-gray-200 rounded-md shadow-sm ${selected ? 'flex' : 'hidden group-hover:flex'}`}
      >
        <span className="px-1 text-[10px] uppercase tracking-wide text-gray-400">{BLOCK_LABELS[block.type]}</span>
        <button type="button" title="Move up" disabled={index === 0} onClick={() => move(index, index - 1)} className="p-1 text-gray-500 hover:text-gray-900 disabled:opacity-30">
          <ChevronUp className="h-4 w-4" />
        </button>
        <button type="button" title="Move down" disabled={index === count - 1} onClick={() => move(index, index + 1)} className="p-1 text-gray-500 hover:text-gray-900 disabled:opacity-30">
          <ChevronDown className="h-4 w-4" />
        </button>
        <button type="button" title="Duplicate" onClick={() => duplicate(block.id)} className="p-1 text-gray-500 hover:text-gray-900">
          <Copy className="h-4 w-4" />
        </button>
        <button type="button" title="Delete" onClick={() => remove(block.id)} className="p-1 text-red-500 hover:text-red-700">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      <div className="flex">
        <button
          type="button"
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          title="Drag to reorder"
          className="shrink-0 w-6 flex items-center justify-center text-gray-300 hover:text-gray-600 cursor-grab touch-none rounded-l-lg"
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <div className="flex-1 min-w-0">
          <View block={block} update={(patch) => update(block.id, patch)} selected={selected} split={block.type === 'html' ? () => splitBlock(block.id) : undefined} />
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Main component                                                      */
/* ------------------------------------------------------------------ */

/**
 * Drag-and-drop email editor.
 *
 * value / onChange work with a plain HTML string so it is a drop-in
 * replacement for the old code box. The block layout is stored inside an
 * HTML comment so emails built here can be re-opened visually later.
 */
const EmailBuilder = ({ value, onChange, variables = [] }) => {
  const initial = useMemo(() => interpretValue(value), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [mode, setMode] = useState(initial.mode);
  const [blocks, setBlocks] = useState(initial.blocks || []);
  const [selectedId, setSelectedId] = useState(null);
  const [activeDrag, setActiveDrag] = useState(null);
  const [hint, setHint] = useState('');
  const lastEmitted = useRef(typeof value === 'string' ? value : '');
  const lastFocus = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const emit = useCallback(
    (html) => {
      lastEmitted.current = html;
      onChange(html);
    },
    [onChange]
  );

  // The parent replaced the content (switching email type, AI draft, loading from the server).
  useEffect(() => {
    const incoming = typeof value === 'string' ? value : '';
    if (incoming === lastEmitted.current) return;
    lastEmitted.current = incoming;
    const next = interpretValue(incoming);
    setMode(next.mode);
    setBlocks(next.blocks || []);
    setSelectedId(null);
  }, [value]);

  const commit = (next) => {
    setBlocks(next);
    emit(blocksToHtml(next));
  };

  const update = (id, patch) => commit(blocks.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  const remove = (id) => commit(blocks.filter((b) => b.id !== id));
  const move = (from, to) => commit(arrayMove(blocks, from, to));
  const duplicate = (id) => {
    const i = blocks.findIndex((b) => b.id === id);
    if (i < 0) return;
    const copy = { ...JSON.parse(JSON.stringify(blocks[i])), id: uid() };
    const next = [...blocks];
    next.splice(i + 1, 0, copy);
    commit(next);
    setSelectedId(copy.id);
  };

  const splitBlock = (id) => {
    const i = blocks.findIndex((b) => b.id === id);
    if (i < 0) return;
    const parts = htmlToBlocks(blocks[i].html);
    if (!parts.length || (parts.length === 1 && parts[0].type === 'html')) return;
    const next = [...blocks];
    next.splice(i, 1, ...parts);
    commit(next);
  };

  const addAt = (item, index) => {
    const block = newBlock(item.type, item.preset);
    const next = [...blocks];
    next.splice(index === undefined ? next.length : index, 0, block);
    commit(next);
    setSelectedId(block.id);
  };

  const handleDragEnd = ({ active, over }) => {
    setActiveDrag(null);
    if (!over) return;
    const fromPalette = active.data.current?.palette;
    if (fromPalette) {
      if (over.id === 'canvas') return addAt(fromPalette);
      const overIndex = blocks.findIndex((b) => b.id === over.id);
      if (overIndex < 0) return addAt(fromPalette);
      const translated = active.rect.current.translated;
      const below = translated && translated.top + translated.height / 2 > over.rect.top + over.rect.height / 2;
      return addAt(fromPalette, overIndex + (below ? 1 : 0));
    }
    if (active.id === over.id) return;
    const from = blocks.findIndex((b) => b.id === active.id);
    const to = blocks.findIndex((b) => b.id === over.id);
    if (from >= 0 && to >= 0) move(from, to);
  };

  // Remember which text box was last used so a variable chip knows where to go.
  const trackFocus = (e) => {
    const t = e.target;
    if (t.isContentEditable || (t.matches && t.matches('input[type="text"], textarea'))) {
      lastFocus.current = t;
    }
  };

  const insertVariable = (text) => {
    const el = lastFocus.current;
    if (!el || !document.body.contains(el)) {
      setHint('Click inside a text box in your email first, then pick a variable.');
      return;
    }
    setHint('');
    el.focus();
    if (el.isContentEditable) {
      document.execCommand('insertText', false, text);
      return;
    }
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? start;
    const setter = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(el), 'value').set;
    setter.call(el, el.value.slice(0, start) + text + el.value.slice(end));
    el.setSelectionRange(start + text.length, start + text.length);
    el.dispatchEvent(new Event('input', { bubbles: true }));
  };

  /* ---- mode switching ---- */

  const toVisual = () => {
    const current = typeof value === 'string' ? value : '';
    const next = interpretValue(current);
    if (next.mode === 'visual') {
      setBlocks(next.blocks);
    } else {
      // Hand-written HTML: split it into sections; anything unrecognised stays as its own Custom HTML section.
      const converted = htmlToBlocks(current);
      setBlocks(converted);
      emit(blocksToHtml(converted));
    }
    setSelectedId(null);
    setMode('visual');
  };

  const toCode = () => setMode('html');

  const isHandWritten = mode === 'html' && interpretValue(value).mode === 'html' && (value || '').trim() !== '';

  return (
    <div>
      <style>{`
        .email-builder-rich { font-family: Arial, Helvetica, sans-serif; color: #4b5563; }
        .email-builder-rich b, .email-builder-rich strong, .email-builder-rich [style*="font-weight"] { font-weight: 700 !important; color: #111827 !important; }
        .email-builder-rich i, .email-builder-rich em { font-style: italic !important; }
        .email-builder-rich u { text-decoration: underline !important; }
        .email-builder-rich ul { list-style: disc; padding-left: 1.5rem; margin: 0 0 .75rem; }
        .email-builder-rich ol { list-style: decimal; padding-left: 1.5rem; margin: 0 0 .75rem; }
        .email-builder-rich p { margin: 0 0 .75rem; }
        .email-builder-rich a { color: #0d9488; text-decoration: underline; }
      `}</style>
      <div className="flex items-center justify-between mb-3">
        <div className="inline-flex border border-gray-300 rounded-lg overflow-hidden text-sm">
          <button
            type="button"
            onClick={toVisual}
            className={`px-3 py-1.5 ${mode === 'visual' ? 'bg-teal-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
          >
            Visual editor
          </button>
          <button
            type="button"
            onClick={toCode}
            className={`px-3 py-1.5 border-l border-gray-300 ${mode === 'html' ? 'bg-teal-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
          >
            HTML code
          </button>
        </div>
        {mode === 'visual' && (
          <span className="text-xs text-gray-500 hidden sm:inline">Drag blocks from the left into your email, or click one to add it.</span>
        )}
      </div>

      {mode === 'html' ? (
        <div>
          {isHandWritten && (
            <div className="mb-3 p-3 text-sm bg-amber-50 border border-amber-200 rounded-lg text-amber-800 flex flex-wrap items-center gap-3">
              <span>This email uses hand-written HTML.</span>
              <button type="button" onClick={toVisual} className="px-3 py-1 text-sm bg-amber-600 text-white rounded-md hover:bg-amber-700">
                Open in visual editor
              </button>
              <span className="text-xs">It will be split into sections you can edit; parts that can't be converted stay as Custom HTML sections.</span>
            </div>
          )}
          <CodeEditor
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => emit(typeof e === 'string' ? e : e?.target?.value ?? '')}
            language="html"
            height="360px"
            placeholder="Enter your email HTML here..."
          />
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={({ active }) => setActiveDrag(active.data.current?.palette || null)}
          onDragCancel={() => setActiveDrag(null)}
          onDragEnd={handleDragEnd}
        >
          <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Content blocks</p>
              <div className="grid grid-cols-3 md:grid-cols-2 gap-2">
                {PALETTE.map((item) => (
                  <PaletteItem key={item.key} item={item} onAdd={(i) => addAt(i)} />
                ))}
              </div>

            </div>

            <Canvas onFocus={trackFocus} onBackgroundClick={() => setSelectedId(null)}>
              <div className="max-w-[600px] mx-auto space-y-3 pt-2">
                {blocks.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center text-gray-500 bg-white border-2 border-dashed border-gray-300 rounded-lg">
                    <p className="font-medium">Your email is empty</p>
                    <p className="text-sm mt-1">Drag a block here to get started.</p>
                  </div>
                ) : (
                  <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
                    {blocks.map((block, i) => (
                      <SortableBlock
                        key={block.id}
                        block={block}
                        index={i}
                        count={blocks.length}
                        selected={selectedId === block.id}
                        onSelect={() => setSelectedId(block.id)}
                        update={update}
                        remove={remove}
                        duplicate={duplicate}
                        move={move}
                        splitBlock={splitBlock}
                      />
                    ))}
                  </SortableContext>
                )}
              </div>
            </Canvas>
          </div>

          {variables.length > 0 && (
            <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Personalise</p>
              <p className="text-xs text-gray-500 mt-1 mb-2">Click inside a text box in your email, then click a name below to insert it.</p>
              <div className="flex flex-wrap gap-1.5">
                {variables.map((v) => (
                  <button
                    key={v.variable}
                    type="button"
                    title={v.description}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => insertVariable(v.variable)}
                    className="px-2 py-1 text-xs font-medium bg-teal-100 text-teal-700 rounded hover:bg-teal-200"
                  >
                    {v.variable}
                  </button>
                ))}
              </div>
              {hint && <p className="mt-2 text-xs text-amber-700">{hint}</p>}
            </div>
          )}

          <DragOverlay>
            {activeDrag ? (
              <div className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-white border border-teal-500 rounded-lg shadow-lg">
                <activeDrag.icon className="h-4 w-4" />
                {activeDrag.label}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  );
};

export default EmailBuilder;
